import { useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import moment, { type Moment } from "moment";

import { DatePicker, type CalendarLevel } from "@mantine/dates";
import { type FixedDateInterval } from "@north-beam/nb-common";
import { PrimaryButton, TertiaryButton } from "@shared/buttons";

import { DateInput } from "../../v2/date-input/date-input";

import "./index.scss";

export type DateRangeCalendarValue = {
  startDate: Moment | string | null;
  endDate: Moment | string | null;
};

interface Props {
  readonly value: DateRangeCalendarValue | null;
  readonly onApply: (value: FixedDateInterval) => void;
  readonly maxLookbackDays?: number;
  readonly onCancel?: () => void;
  readonly className?: string;
  readonly maxAllowedDate?: Moment;
}

const parseDateInClientTimezone = (
  value: Moment | string | null,
): Moment | null => {
  if (typeof value === "string") {
    const mmt = moment.tz(value, moment.tz.guess());
    if (!mmt.isValid()) {
      throw new Error(`The value '${value}' is invalid!`);
    }
    return mmt;
  }

  return value;
};

export const DateRangeCalendar = ({
  value,
  maxLookbackDays,
  maxAllowedDate,
  onApply,
  onCancel,
  className,
}: Props) => {
  const clientTimeZone = moment.tz.guess();

  const [_value, setValue] = useState<{
    startDate: Moment | null;
    endDate: Moment | null;
  }>({
    startDate: null,
    endDate: null,
  });

  const [calendarLevel, setCalendarLevel] = useState<CalendarLevel>("month");

  useEffect(() => {
    setValue(
      value === null
        ? { startDate: null, endDate: null }
        : {
            startDate: parseDateInClientTimezone(value.startDate),
            endDate: parseDateInClientTimezone(value.endDate),
          },
    );
  }, [value]);

  const defaultDate = useMemo(() => {
    const endDate = parseDateInClientTimezone(value?.endDate ?? null);

    return (endDate ? endDate : moment().tz(clientTimeZone))
      .startOf("month")
      .subtract(1, "month")
      .toDate();
  }, [value, clientTimeZone]);

  const minDate =
    maxLookbackDays !== undefined
      ? moment()
          .tz(clientTimeZone)
          .startOf("day")
          .subtract(maxLookbackDays - 1, "days")
      : undefined;
  const maxDate =
    maxLookbackDays && !maxAllowedDate
      ? moment().tz(clientTimeZone).startOf("day")
      : maxAllowedDate
      ? maxAllowedDate
      : undefined;

  return (
    <div className={clsx("text-sm", className)}>
      <div className="pt-4 px-4">
        <div className="flex items-center gap-2.5 text-sm mb-3">
          <DateInput
            className="flex-1"
            value={_value.startDate}
            minDate={minDate}
            maxDate={maxDate}
            placeholder="Begins..."
            onChange={(date) => {
              if (!_value.endDate || _value.endDate.isSameOrAfter(date)) {
                setValue({ ..._value, startDate: date });
              }
            }}
          />
          <i className="fa-regular fa-arrow-right text-sm text-gray" />
          <DateInput
            className="flex-1"
            value={_value.endDate}
            minDate={minDate}
            maxDate={maxDate}
            placeholder="Ends..."
            onChange={(date) => {
              if (!_value.startDate || _value.startDate.isSameOrBefore(date)) {
                setValue({ ..._value, endDate: date });
              }
            }}
          />
        </div>
        <DatePicker
          className="date-range-calendar"
          classNames={{
            calendarHeader: "calendar-header",
            calendarHeaderLevel: "calendar-header-level",
            calendarHeaderControl: "calendar-header-control",
            levelsGroup: "levels-group",
            yearsList: "years-list",
            yearsListCell: "years-list-cell",
            monthsList: "months-list",
            monthsListCell: "months-list-cell",
            monthCell: "month-cell",
            month: "month",
            weekday: "weekday",
            day: "day",
          }}
          getYearControlProps={(day) => {
            const year = moment.tz(day, clientTimeZone).startOf("year");
            const currentYear = moment().tz(clientTimeZone).startOf("year");
            const selectedYearStart =
              _value.startDate && moment(_value.startDate).startOf("year");
            const selectedYearEnd =
              _value.endDate && moment(_value.endDate).startOf("year");

            return {
              className: clsx({
                "in-future": year.isAfter(currentYear),
                current: year.isSame(currentYear),
                "in-range":
                  selectedYearStart &&
                  selectedYearStart.isSameOrBefore(year) &&
                  selectedYearEnd &&
                  selectedYearEnd.isSameOrAfter(year),
                "first-in-range":
                  selectedYearStart && selectedYearStart.isSame(year),
                "last-in-range":
                  selectedYearEnd && selectedYearEnd.isSame(year),
              }),
            };
          }}
          getMonthControlProps={(day) => {
            const month = moment.tz(day, clientTimeZone).startOf("month");
            const currentMonth = moment().tz(clientTimeZone).startOf("month");
            const selectedMonthStart =
              _value.startDate && moment(_value.startDate).startOf("month");
            const selectedMonthEnd =
              _value.endDate && moment(_value.endDate).startOf("month");

            return {
              className: clsx({
                "in-future": month.isAfter(currentMonth),
                current: month.isSame(currentMonth),
                "in-range":
                  selectedMonthStart &&
                  selectedMonthStart.isSameOrBefore(month) &&
                  selectedMonthEnd &&
                  selectedMonthEnd.isSameOrAfter(month),
                "first-in-range":
                  selectedMonthStart && selectedMonthStart.isSame(month),
                "last-in-range":
                  selectedMonthEnd && selectedMonthEnd.isSame(month),
              }),
            };
          }}
          getDayProps={(day) => ({
            className: clsx("text-v2-primary", {
              "in-future text-v2-secondary": moment
                .tz(day, clientTimeZone)
                .isAfter(moment().tz(clientTimeZone)),
            }),
          })}
          type="range"
          numberOfColumns={2}
          columnsToScroll={1}
          firstDayOfWeek={0}
          previousIcon={
            <i className="fa-regular fa-arrow-left text-xs text-cosmic" />
          }
          allowSingleDateInRange
          nextIcon={
            <i className="fa-regular fa-arrow-right text-xs text-cosmic" />
          }
          defaultDate={defaultDate}
          minDate={minDate?.toDate()}
          maxDate={maxDate?.toDate()}
          level={calendarLevel}
          value={[
            _value.startDate ? _value.startDate.toDate() : null,
            _value.endDate ? _value.endDate.toDate() : null,
          ]}
          onChange={(val) => {
            setValue({
              startDate: val[0] ? moment.tz(val[0], clientTimeZone) : null,
              endDate: val[1] ? moment.tz(val[1], clientTimeZone) : null,
            });
          }}
          onLevelChange={setCalendarLevel}
        />
      </div>
      <div className="flex justify-end gap-2 border-t border-gray-2 p-4">
        {calendarLevel === "month" ? (
          <>
            {onCancel && (
              <TertiaryButton
                className="font-bold"
                onClick={() => {
                  onCancel();
                }}
              >
                CANCEL
              </TertiaryButton>
            )}
            <PrimaryButton
              className="font-bold"
              disabled={!_value.startDate || !_value.endDate}
              onClick={() => {
                if (_value.startDate && _value.endDate) {
                  onApply({
                    type: "fixed",
                    startDate: _value.startDate.format("YYYY-MM-DD"),
                    endDate: _value.endDate.format("YYYY-MM-DD"),
                  });
                }
              }}
            >
              APPLY
            </PrimaryButton>
          </>
        ) : (
          <TertiaryButton
            className="font-bold"
            onClick={() => {
              setCalendarLevel("month");
            }}
          >
            RETURN TO CALENDAR
          </TertiaryButton>
        )}
      </div>
    </div>
  );
};
